// Motor de distribución de cuentas/salas Zoom. Lógica pura (sin React ni Firestore) para que
// "Distribuir un día" y "Analizar un periodo" usen exactamente las mismas reglas, y para testearla.
//
// Reglas reales para detectar cruces (NO modifican el calendario):
// • Masterclass: se ignora el end guardado (suele decir 1h porque representa la "clase modelo").
//   La MC real dura 1h40-1h50min; usamos 110min como cota superior.
// • Clase: se respeta el end guardado (2h, 3h, +) y se añaden 10min de buffer entre salas.
// • Cada cuenta soporta hasta MAX_SIMULTANEOUS_PER_ACCOUNT reuniones a la vez.
// • Una sala nunca se asigna a dos eventos que se cruzan.
// • Reuso: una clase puede usar la sala de una MC que empezó a la hora `reuseMcHour` (17:00) ese
//   mismo día, de cualquier curso, cuando esa MC ya terminó.

export const MAX_SIMULTANEOUS_PER_ACCOUNT = 2;
export const MASTERCLASS_DURATION_MIN = 110;
export const CLASE_BUFFER_MIN = 10;
export const CLASE_FALLBACK_MIN = 120;
export const STRONG_MATCH = 200;
const COURSE_ID_MATCH = 100000;

export const DEFAULT_DIST_OPTIONS = Object.freeze({ reuseMcRooms: true, reuseMcHour: 17 });

export const toLocalDateStr = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

export const getEventType = (e) => e?.type || e?.resource?.type || 'clase';
export const getEventZoomAccountId = (e) => e?.zoomAccountId || e?.resource?.zoomAccountId || null;
const eventCourseId = (e) => e?.courseId || e?.resource?.courseId || null;

export const fmtTimeLabel = (mins) =>
    `${Math.floor(mins / 60) % 12 || 12}:${String(mins % 60).padStart(2, '0')} ${mins >= 720 ? 'pm' : 'am'}`;

// Line-sweep: pico real de intervalos activos a la vez (A→B→C encadenados dan 2, no 3).
// Salidas antes que entradas en el mismo instante: back-to-back no cuenta como cruce.
export const peakSimultaneous = (intervals) => {
    const points = [];
    intervals.forEach(o => {
        points.push({ time: o.effectiveStartMin, delta: +1 });
        points.push({ time: o.effectiveEndMin, delta: -1 });
    });
    points.sort((a, b) => a.time - b.time || a.delta - b.delta);
    let cur = 0, peak = 0;
    points.forEach(p => { cur += p.delta; if (cur > peak) peak = cur; });
    return peak;
};

// Pico de simultáneas dentro de la ventana de un evento concreto.
export const concurrentDuring = (target, all) => peakSimultaneous(
    all.map(o => ({
        effectiveStartMin: Math.max(o.effectiveStartMin, target.effectiveStartMin),
        effectiveEndMin: Math.min(o.effectiveEndMin, target.effectiveEndMin),
    })).filter(o => o.effectiveStartMin < o.effectiveEndMin)
);

// Franjas donde hay más de `limit` intervalos activos: [{ fromMin, toMin, count }].
export const windowsOver = (intervals, limit) => {
    const points = [];
    intervals.forEach(o => {
        points.push({ time: o.effectiveStartMin, delta: +1 });
        points.push({ time: o.effectiveEndMin, delta: -1 });
    });
    points.sort((a, b) => a.time - b.time || a.delta - b.delta);
    const out = [];
    let cur = 0, open = null;
    points.forEach(p => {
        cur += p.delta;
        if (cur > limit) {
            if (!open) open = { fromMin: p.time, count: cur };
            else open.count = Math.max(open.count, cur);
        } else if (open) {
            if (p.time > open.fromMin) out.push({ ...open, toMin: p.time });
            open = null;
        }
    });
    return out;
};

// ── Emparejamiento sala ↔ curso ─────────────────────────────────────────────
export const normForMatch = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[-:_.,()]/g, ' ').replace(/\s+/g, ' ').trim();

export const usablePool = (pool) => (pool || []).filter(m => !m.zoomMissing);

export const scoreMeetingMatch = (meeting, courseName, eventTitle, courseId = null) => {
    if (courseId && meeting?.courseId === courseId) return COURSE_ID_MATCH;
    const cName = normForMatch(courseName);
    const tName = normForMatch(eventTitle).replace(/^(mc|cl|masterclass|clase)\s+/, '');
    const mName = normForMatch(meeting?.name).replace(/^(masterclass|clase)\s*/, '');
    let score = 0;
    if (cName && mName && (mName.includes(cName) || cName.includes(mName))) score += 1000;
    if (tName && mName && (mName.includes(tName) || tName.includes(mName))) score += 500;
    const cWords = cName.split(' ').filter(w => w.length > 3);
    cWords.forEach(w => { if (mName.includes(w)) score += 10; });
    if (cWords.length > 0) {
        const matched = cWords.filter(w => mName.includes(w)).length;
        score += Math.round((matched / cWords.length) * 200);
    }
    return score;
};

export const pickBestMeeting = (pool, courseName, eventTitle, courseId = null) => {
    const usable = usablePool(pool);
    if (usable.length === 0) return null;
    if (usable.length === 1) return usable[0];
    return usable
        .map(m => ({ m, s: scoreMeetingMatch(m, courseName, eventTitle, courseId) }))
        .sort((a, b) => b.s - a.s)[0].m;
};

// ¿La cuenta tiene una sala de ese tipo que corresponde al curso?
// Mismo courseId, nombre contenido, o ≥80% de las palabras (>3 letras) del curso.
export const accountHasMeetingForCourse = (account, type, courseName, eventTitle, courseId = null) => {
    const pool = usablePool(type === 'masterclass' ? account.meetings : account.claseMeetings);
    if (pool.length === 0) return false;
    if (courseId && pool.some(m => m.courseId === courseId)) return true;
    const cName = normForMatch(courseName);
    const tName = normForMatch(eventTitle).replace(/^(mc|cl|masterclass|clase)\s+/, '');
    const cWords = cName.split(' ').filter(w => w.length > 3);
    return pool.some(m => {
        const mName = normForMatch(m.name).replace(/^(masterclass|clase)\s*/, '');
        if (cName && mName && (mName.includes(cName) || cName.includes(mName))) return true;
        if (tName && mName && (mName.includes(tName) || tName.includes(mName))) return true;
        if (cWords.length >= 2) {
            const matched = cWords.filter(w => mName.includes(w)).length;
            if (matched / cWords.length >= 0.8) return true;
        }
        return false;
    });
};

// ── Intervalos ──────────────────────────────────────────────────────────────
export const buildInterval = (e) => {
    const s = e.start instanceof Date ? e.start : new Date(e.start);
    const end = e.end instanceof Date ? e.end : new Date(e.end);
    const startMin = s.getHours() * 60 + s.getMinutes();
    let endMin = end.getHours() * 60 + end.getMinutes();
    const type = getEventType(e);
    let effectiveEndMin;
    if (type === 'masterclass') {
        effectiveEndMin = startMin + MASTERCLASS_DURATION_MIN;
        if (endMin <= startMin) endMin = startMin + MASTERCLASS_DURATION_MIN;
    } else {
        if (endMin <= startMin) endMin = startMin + CLASE_FALLBACK_MIN;
        effectiveEndMin = endMin + CLASE_BUFFER_MIN;
    }
    return { event: e, type, startMin, endMin, effectiveStartMin: startMin, effectiveEndMin, existingAccountId: getEventZoomAccountId(e) };
};

export const dayEventsOf = (events, dateStr) => (events || [])
    .filter(e => e.status !== 'cancelled' && toLocalDateStr(e.start) === dateStr)
    .sort((a, b) => new Date(a.start) - new Date(b.start));

const overlaps = (a, b) => a.effectiveStartMin < b.effectiveEndMin && b.effectiveStartMin < a.effectiveEndMin;

const baseAssignment = (iv) => ({
    eventId: iv.event.id,
    eventTitle: iv.event.title,
    eventType: iv.type,
    courseId: eventCourseId(iv.event),
    startMin: iv.startMin,
    endMin: iv.endMin,
    effectiveStartMin: iv.effectiveStartMin,
    effectiveEndMin: iv.effectiveEndMin,
    startLabel: fmtTimeLabel(iv.startMin),
    endLabel: fmtTimeLabel(iv.endMin),
    needsZoomAssignment: !!(iv.event.needsZoomAssignment ?? iv.event.resource?.needsZoomAssignment),
});

const evField = (e, k) => (e?.[k] != null ? e[k] : e?.resource?.[k]);
const linkFieldsOf = (e, type) => type === 'masterclass'
    ? { zoomLink: evField(e, 'mcLiveUrl') || '', zoomMeetingId: evField(e, 'mcMeetingId') || '', zoomPassword: evField(e, 'mcPassword') || '' }
    : { zoomLink: evField(e, 'claseLiveUrl') || '', zoomMeetingId: evField(e, 'claseMeetingId') || '', zoomPassword: evField(e, 'clasePassword') || '' };
const sameZoomId = (a, b) => !!a && !!b && String(a).replace(/\D/g, '') === String(b).replace(/\D/g, '');

// Asignación a partir de lo YA guardado en el evento (cuenta + link). null si no tiene cuenta.
// Una clase guardada con la sala de una MC se reconoce como sala prestada (reusedMcRoom).
export const existingAssignment = (e, accounts) => {
    const accountId = getEventZoomAccountId(e);
    if (!accountId) return null;
    const iv = buildInterval(e);
    const account = (accounts || []).find(z => z.id === accountId);
    const link = linkFieldsOf(e, iv.type);
    const match = (m) => (link.zoomMeetingId && sameZoomId(m.meetingId, link.zoomMeetingId)) || (link.zoomLink && m.zoomLink === link.zoomLink);
    let mtg = ((iv.type === 'masterclass' ? account?.meetings : account?.claseMeetings) || []).find(match);
    let reusedMcRoom = false;
    if (!mtg && iv.type !== 'masterclass') {
        mtg = (account?.meetings || []).find(match);
        reusedMcRoom = !!mtg;
    }
    return {
        ...baseAssignment(iv),
        accountId,
        accountName: account?.name || evField(e, 'zoomAccountName') || '(cuenta no encontrada)',
        accountEmail: account?.email || '',
        accountColor: account?.color || '#6b7280',
        meetingId: mtg?.id || null,
        meetingName: mtg?.name || '',
        ...link,
        ...(reusedMcRoom ? { reusedMcRoom: true } : {}),
    };
};

// ── Distribuir un día ───────────────────────────────────────────────────────
// dayEvents: eventos del día (sin cancelados). lockedEventIds + currentResult: las asignaciones
// bloqueadas se conservan tal cual y ocupan su cuenta y su sala desde el inicio.
// Devuelve { assignments, warnings } con assignments en orden cronológico.
export const distributeDay = ({
    dayEvents, accounts, courses, lockedEventIds = new Set(), currentResult = null, options = DEFAULT_DIST_OPTIONS,
}) => {
    const opts = { ...DEFAULT_DIST_OPTIONS, ...(options || {}) };
    const warnings = [];
    if (!dayEvents?.length || !accounts?.length) return { assignments: [], warnings };

    const intervals = dayEvents.map(buildInterval);
    const courseNameOf = (cid) => (courses || []).find(c => c.id === cid)?.name || '';
    const slots = accounts.map(account => ({ account, occupied: [] }));
    const slotOf = (id) => slots.find(s => s.account.id === id);
    const placed = []; // asignaciones ya decididas (bloqueadas + nuevas), para ver salas ocupadas

    const occupy = (slot, a) => slot.occupied.push({ effectiveStartMin: a.effectiveStartMin, effectiveEndMin: a.effectiveEndMin });
    const fitsIfAdded = (slot, iv) =>
        peakSimultaneous([...slot.occupied, iv]) <= MAX_SIMULTANEOUS_PER_ACCOUNT;
    const sortByLoad = (list) => list.slice().sort((a, b) => {
        if (a.occupied.length !== b.occupied.length) return a.occupied.length - b.occupied.length;
        const la = a.occupied.reduce((m, o) => Math.max(m, o.effectiveEndMin), 0);
        const lb = b.occupied.reduce((m, o) => Math.max(m, o.effectiveEndMin), 0);
        return la - lb;
    });

    // 1) Bloqueadas primero: ocupan cuenta y sala aunque empiecen más tarde.
    const lockedById = new Map();
    if (currentResult && lockedEventIds.size > 0) {
        for (const a of currentResult) {
            if (!lockedEventIds.has(a.eventId) || !intervals.some(iv => iv.event.id === a.eventId)) continue;
            lockedById.set(a.eventId, a);
            placed.push(a);
            const slot = a.accountId && slotOf(a.accountId);
            if (slot) occupy(slot, a);
        }
    }

    // Sala ocupada en el intervalo por otra asignación de la misma cuenta.
    const roomBusy = (accountId, room, iv) => placed.some(p =>
        p.accountId === accountId && overlaps(p, iv) &&
        ((p.meetingId && p.meetingId === room.id) ||
         (p.zoomMeetingId && room.meetingId && String(p.zoomMeetingId).replace(/\D/g, '') === String(room.meetingId).replace(/\D/g, ''))));

    // Salas de MC reutilizables para una clase: MC de esta cuenta que empezó a la hora configurada
    // y ya terminó antes de que empiece la clase, y cuya sala está libre en ese horario.
    const reusableMcRooms = (slot, iv) => {
        if (!opts.reuseMcRooms || iv.type === 'masterclass') return [];
        const hourMin = opts.reuseMcHour * 60;
        const ids = new Set(placed
            .filter(p => p.accountId === slot.account.id && p.eventType === 'masterclass' && p.meetingId &&
                p.startMin >= hourMin && p.startMin < hourMin + 60 && p.effectiveEndMin <= iv.effectiveStartMin)
            .map(p => p.meetingId));
        return usablePool(slot.account.meetings).filter(m => ids.has(m.id) && !roomBusy(slot.account.id, m, iv));
    };

    // Elige la sala dentro de una cuenta. null = no hay sala libre.
    const roomFor = (slot, iv) => {
        const courseId = eventCourseId(iv.event);
        const courseName = courseNameOf(courseId);
        const pool = usablePool(iv.type === 'masterclass' ? slot.account.meetings : slot.account.claseMeetings);
        const available = pool.filter(m => !roomBusy(slot.account.id, m, iv));
        const strong = pickBestMeeting(available, courseName, iv.event.title, courseId);
        if (strong && scoreMeetingMatch(strong, courseName, iv.event.title, courseId) >= STRONG_MATCH) return { meeting: strong, strong: true };
        if (slot.account.id === iv.existingAccountId) {
            const prevId = iv.event.claseMeetingId || iv.event.mcMeetingId;
            const prevLink = iv.event.claseLiveUrl || iv.event.mcLiveUrl;
            const prev = available.find(m => (prevId && m.meetingId === prevId) || (prevLink && m.zoomLink === prevLink));
            if (prev && scoreMeetingMatch(prev, courseName, iv.event.title, courseId) >= STRONG_MATCH) return { meeting: prev, strong: true };
        }
        const reuse = reusableMcRooms(slot, iv);
        if (reuse.length) return { meeting: reuse[0], reusedMcRoom: true };
        if (available.length) return { meeting: pickBestMeeting(available, courseName, iv.event.title, courseId), weak: true };
        // Cuenta previa sin sala libre en su lista: conservar el link manual que ya tenía el evento.
        if (slot.account.id === iv.existingAccountId) {
            const raw = linkFieldsOf(iv.event, iv.type);
            if (raw.zoomLink) return { raw };
        }
        return null;
    };

    const canHost = (slot, iv) => fitsIfAdded(slot, iv) && !!roomFor(slot, iv);

    const pickSlot = (iv) => {
        const courseId = eventCourseId(iv.event);
        const courseName = courseNameOf(courseId);
        const title = iv.event.title;
        const compatible = slots.filter(s => accountHasMeetingForCourse(s.account, iv.type, courseName, title, courseId));
        if (!compatible.length) {
            warnings.push({
                type: 'no-meeting',
                text: `"${title}": ninguna cuenta de Zoom tiene la sala configurada para "${courseName || title}". Crea la sala primero.`,
                courseId: courseId || null, courseName, eventType: iv.type,
            });
        }
        const priority = courseId ? slots.filter(s => (s.account.priorityCourseIds || []).includes(courseId)) : [];

        // 1) Cuenta priorizada por el usuario que además tiene sala.
        if (compatible.length && priority.length) {
            const prioCompat = priority.filter(s => compatible.includes(s));
            if (prioCompat.length) {
                const fits = prioCompat.filter(s => canHost(s, iv));
                if (fits.length) {
                    if (prioCompat.length > 1) warnings.push({ type: 'multi-priority', text: `"${title}": ${prioCompat.length} cuentas priorizan este curso. Se eligió la de menor carga.` });
                    return sortByLoad(fits)[0];
                }
                warnings.push({ type: 'priority-saturated', text: `"${title}": cuenta(s) priorizada(s) saturada(s) en su horario. Asignando otra compatible.` });
            } else {
                warnings.push({ type: 'priority-no-meeting', text: `"${title}": cuentas priorizadas no tienen la sala "${courseName}". Asignando otra compatible.` });
            }
        }
        // 2) Cuenta previa compatible (respetar la elección anterior).
        const existing = iv.existingAccountId ? slotOf(iv.existingAccountId) : null;
        if (existing && compatible.includes(existing) && canHost(existing, iv)) return existing;
        // 3) Balance de carga entre compatibles.
        if (compatible.length) {
            const candidates = compatible.filter(s => canHost(s, iv));
            if (candidates.length) {
                const winner = sortByLoad(candidates)[0];
                if (existing && !compatible.includes(existing)) {
                    warnings.push({ type: 'reassigned-to-compatible', text: `"${title}": reasignado a "${winner.account.name}" porque "${existing.account.name}" no tiene la sala para "${courseName}".` });
                }
                return winner;
            }
            warnings.push({ type: 'all-compatible-saturated', text: `"${title}": las ${compatible.length} cuenta(s) compatibles están ocupadas en este horario.`, courseId, courseName, eventType: iv.type });
        }
        // 4) Cuenta previa aunque no tenga la sala del curso (mejor que dejarlo sin cuenta).
        if (existing && canHost(existing, iv)) {
            warnings.push({ type: 'existing-no-meeting', text: `"${title}": "${existing.account.name}" no tiene sala para "${courseName}", pero se mantiene la asignación previa.` });
            return existing;
        }
        // 5) Último recurso: una cuenta con sala de MC de las 5 pm ya libre (cualquier curso).
        const borrowers = slots.filter(s => reusableMcRooms(s, iv).length > 0 && fitsIfAdded(s, iv));
        if (borrowers.length) return sortByLoad(borrowers)[0];
        return null;
    };

    const fresh = [];
    for (const iv of intervals) {
        if (lockedById.has(iv.event.id)) continue;
        const slot = pickSlot(iv);
        if (!slot) {
            fresh.push({
                ...baseAssignment(iv),
                accountId: null, accountName: '⚠️ Sin cuenta disponible', accountEmail: '', accountColor: '#ef4444',
                meetingId: null, meetingName: '', zoomLink: '', zoomMeetingId: '', zoomPassword: '',
            });
            continue;
        }
        const room = roomFor(slot, iv);
        const m = room.meeting || {};
        const a = {
            ...baseAssignment(iv),
            accountId: slot.account.id,
            accountName: slot.account.name,
            accountEmail: slot.account.email,
            accountColor: slot.account.color,
            meetingId: m.id || null,
            meetingName: m.name || '',
            zoomLink: m.zoomLink || '',
            zoomMeetingId: m.meetingId || '',
            zoomPassword: m.password || '',
            ...(room.raw || {}),
            ...(room.reusedMcRoom ? { reusedMcRoom: true } : {}),
        };
        if (room.reusedMcRoom) {
            warnings.push({ type: 'reused-mc-room', text: `"${iv.event.title}": usa la sala de masterclass "${m.name}" (libre desde la MC de las ${fmtTimeLabel(opts.reuseMcHour * 60)}). Revisa su grabación: el bot puede tomarla como masterclass.` });
        }
        occupy(slot, a);
        placed.push(a);
        fresh.push(a);
    }

    const order = new Map(intervals.map((iv, i) => [iv.event.id, i]));
    const assignments = [...lockedById.values(), ...fresh].sort((x, y) => order.get(x.eventId) - order.get(y.eventId));
    return { assignments, warnings };
};

// Salas faltantes (curso + tipo) a partir de los avisos, sin repetir.
export const missingRoomItemsFrom = (warnings) => {
    const seen = new Set();
    return (warnings || [])
        .filter(w => w.type === 'no-meeting' && w.courseId && w.courseName)
        .filter(w => { const k = `${w.courseId}|${w.eventType}`; if (seen.has(k)) return false; seen.add(k); return true; })
        .map(w => ({ courseId: w.courseId, courseName: w.courseName, kind: w.eventType === 'masterclass' ? 'masterclass' : 'clase' }));
};

// ── Analizar un periodo ─────────────────────────────────────────────────────
// Recorre cada día con eventos entre fromStr y toStr (YYYY-MM-DD, inclusive) y calcula, con las
// mismas reglas que un día: pico de salas a la vez, cuentas necesarias, franjas que superan la
// capacidad, eventos sin cuenta y salas faltantes. `distribution` trae la propuesta de cada día.
export const analyzePeriod = ({ events, accounts, courses, fromStr, toStr, options = DEFAULT_DIST_OPTIONS }) => {
    const capacity = (accounts?.length || 0) * MAX_SIMULTANEOUS_PER_ACCOUNT;
    const byDay = new Map();
    for (const e of events || []) {
        if (e.status === 'cancelled') continue;
        const d = toLocalDateStr(e.start);
        if (d < fromStr || d > toStr) continue;
        if (!byDay.has(d)) byDay.set(d, []);
        byDay.get(d).push(e);
    }
    const days = [...byDay.keys()].sort().map(date => {
        const dayEvents = byDay.get(date).sort((a, b) => new Date(a.start) - new Date(b.start));
        const intervals = dayEvents.map(buildInterval);
        const peak = peakSimultaneous(intervals);
        const accountsNeeded = Math.ceil(peak / MAX_SIMULTANEOUS_PER_ACCOUNT);
        // Lo ya guardado queda bloqueado: el periodo solo propone cuenta para los eventos nuevos.
        const existing = dayEvents.map(e => existingAssignment(e, accounts)).filter(Boolean);
        const { assignments, warnings } = distributeDay({
            dayEvents, accounts, courses, options,
            lockedEventIds: new Set(existing.map(a => a.eventId)), currentResult: existing,
        });
        const fresh = existing.length ? distributeDay({ dayEvents, accounts, courses, options }).assignments : assignments;
        const savedIds = new Set(existing.map(a => a.eventId));
        // No se tocan: eventos marcados "Sin Zoom" ni eventos con link manual sin cuenta.
        const untouchable = new Set(dayEvents.filter(e =>
            (e.needsZoomAssignment ?? e.resource?.needsZoomAssignment) ||
            (!getEventZoomAccountId(e) && (evField(e, 'mcLiveUrl') || evField(e, 'claseLiveUrl')))).map(e => e.id));
        const savable = (list) => list.filter(a => a.accountId && !untouchable.has(a.eventId));
        const toSave = savable(assignments).filter(a => !savedIds.has(a.eventId));
        const toSaveAll = savable(fresh);
        const unassigned = accounts?.length ? assignments.filter(a => !a.accountId).length : dayEvents.length;
        const critical = windowsOver(intervals, capacity).map(w => ({
            ...w,
            fromLabel: fmtTimeLabel(w.fromMin),
            toLabel: fmtTimeLabel(w.toMin),
            titles: intervals.filter(iv => iv.effectiveStartMin < w.toMin && w.fromMin < iv.effectiveEndMin).map(iv => iv.event.title),
        }));
        const status = accountsNeeded > (accounts?.length || 0) ? 'over'
            : (unassigned > 0 ? 'gaps' : (accountsNeeded === (accounts?.length || 0) ? 'tight' : 'ok'));
        return {
            date, events: dayEvents.length, peak, accountsNeeded, unassigned, critical, status,
            saved: existing.length === 0 ? false : (existing.length === dayEvents.length ? 'all' : 'partial'),
            toSave, toSaveAll,
            reused: assignments.filter(a => a.reusedMcRoom).length,
            missingRooms: missingRoomItemsFrom(warnings),
            assignments, warnings,
        };
    });
    const allMissing = missingRoomItemsFrom(days.flatMap(d => d.warnings));
    return {
        days,
        totals: {
            days: days.length,
            events: days.reduce((s, d) => s + d.events, 0),
            maxPeak: days.reduce((m, d) => Math.max(m, d.peak), 0),
            maxAccountsNeeded: days.reduce((m, d) => Math.max(m, d.accountsNeeded), 0),
            accountsAvailable: accounts?.length || 0,
            overDays: days.filter(d => d.status === 'over').length,
            unassigned: days.reduce((s, d) => s + d.unassigned, 0),
            reused: days.reduce((s, d) => s + d.reused, 0),
            missingRooms: allMissing,
        },
    };
};
